import React, { memo, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Grid,
  Tag,
  Progress,
  Space,
  Button,
  Divider,
  Skeleton,
  Tooltip,
} from "antd";

import {
  ArrowRightOutlined,
  CarOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  FileAddFilled,
  MessageOutlined,
  MinusCircleOutlined,
  RedoOutlined,
  DashboardOutlined,
  FireOutlined,
} from "@ant-design/icons";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";

import useBookingCounts from "../utils/hooks/useBookingCounts";
import { openMenuTab } from "../utils/menu/menutabs";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface Props {
  collapsed?: boolean;
}

interface StatCard {
  key: string;
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  path: string;
  loading: boolean;
  error: string | null;
  rangeLabel: string;
  share?: number;
}

const THEME = {
  primary: "#884699c9",
  primaryDark: "#6b2b80bb",
  primaryLight: "#B56AC8",

  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  info: "#3B82F6",

  background: "#F7F8FC",
  card: "#FFFFFF",

  text: "#1F2937",
  subText: "#6B7280",

  border: "#ECECEC",
};

const SHADOW = "0 12px 35px rgba(15,23,42,.08)";

const CARD_RADIUS = 24;

const CARD_GRADIENTS = [
  "linear-gradient(135deg,#884699,#B56AC8)",
  "linear-gradient(135deg,#2563EB,#60A5FA)",
  "linear-gradient(135deg,#10B981,#34D399)",
  "linear-gradient(135deg,#F59E0B,#FB923C)",
  "linear-gradient(135deg,#EF4444,#FB7185)",
  "linear-gradient(135deg,#06B6D4,#67E8F9)",
];

const revenueData = [
  { month: "Jan", revenue: 4000 },
  { month: "Feb", revenue: 5200 },
  { month: "Mar", revenue: 4600 },
  { month: "Apr", revenue: 6800 },
  { month: "May", revenue: 5900 },
  { month: "Jun", revenue: 7100 },
  { month: "Jul", revenue: 8300 },
];

const bookingData = [
  { day: "Mon", bookings: 32 },
  { day: "Tue", bookings: 41 },
  { day: "Wed", bookings: 28 },
  { day: "Thu", bookings: 55 },
  { day: "Fri", bookings: 49 },
  { day: "Sat", bookings: 71 },
  { day: "Sun", bookings: 44 },
];

const cardStyle: React.CSSProperties = {
  border: "none",
  borderRadius: CARD_RADIUS,
  boxShadow: SHADOW,
  overflow: "hidden",
};

const glassTile: React.CSSProperties = {
  background: "rgba(255,255,255,.14)",
  backdropFilter: "blur(16px)",
  borderRadius: 20,
};


const SampleTag = () => (
  <Tag color="default" style={{ borderRadius: 20, fontWeight: 600 }}>
    Sample data
  </Tag>
);

const Dashboard: React.FC<Props> = () => {
  const screens = useBreakpoint();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const today = new Date();

  const {
    counts,
    totalTrips,
    anyLoading,
    lastUpdated,
    refresh,
  } = useBookingCounts();

  const greeting =
    today.getHours() < 12
      ? "Good Morning"
      : today.getHours() < 17
        ? "Good Afternoon"
        : "Good Evening";

  const dashboardStats: StatCard[] = useMemo(() => {
    const shareOf = (value: number) =>
      totalTrips ? Math.round((value / totalTrips) * 100) : 0;

    return [
      {
        key: "new",
        title: counts.new.label,
        value: String(counts.new.value),
        icon: <FileAddFilled />,
        color: CARD_GRADIENTS[0],
        path: counts.new.path,
        loading: counts.new.loading,
        error: counts.new.error,
        rangeLabel: counts.new.rangeLabel,
        share: shareOf(counts.new.value),
      },
      {
        key: "live",
        title: counts.live.label,
        value: String(counts.live.value),
        icon: <CarOutlined />,
        color: CARD_GRADIENTS[1],
        path: counts.live.path,
        loading: counts.live.loading,
        error: counts.live.error,
        rangeLabel: counts.live.rangeLabel,
        share: shareOf(counts.live.value),
      },
      {
        key: "completed",
        title: counts.completed.label,
        value: String(counts.completed.value),
        icon: <CheckCircleFilled />,
        color: CARD_GRADIENTS[2],
        path: counts.completed.path,
        loading: counts.completed.loading,
        error: counts.completed.error,
        rangeLabel: counts.completed.rangeLabel,
        share: shareOf(counts.completed.value),
      },
      {
        key: "cancel",
        title: counts.cancel.label,
        value: String(counts.cancel.value),
        icon: <MinusCircleOutlined />,
        color: CARD_GRADIENTS[4],
        path: counts.cancel.path,
        loading: counts.cancel.loading,
        error: counts.cancel.error,
        rangeLabel: counts.cancel.rangeLabel,
        share: shareOf(counts.cancel.value),
      },
      {
        key: "enquiry",
        title: counts.enquiry.label,
        value: String(counts.enquiry.value),
        icon: <MessageOutlined />,
        color: CARD_GRADIENTS[5],
        path: counts.enquiry.path,
        loading: counts.enquiry.loading,
        error: counts.enquiry.error,
        rangeLabel: counts.enquiry.rangeLabel,
      },
    ];
  }, [counts, totalTrips]);

  const heroTiles = useMemo(
    () => [
      {
        label: "Driver Calls",
        value: counts.driverCalls.value,
        error: counts.driverCalls.error,
        tag: counts.driverCalls.rangeLabel,
        tagColor: "blue",
      },
      {
        label: counts.availableCabs.label,
        value: counts.availableCabs.value,
        error: counts.availableCabs.error,
        tag: counts.availableCabs.rangeLabel,
        tagColor: "green",
      },
      {
        label: counts.attachment.label,
        value: counts.attachment.value,
        error: counts.attachment.error,
        tag: counts.attachment.rangeLabel,
        tagColor: "orange",
      },
      {
        label: counts.enterprise.label,
        value: counts.enterprise.value,
        error: counts.enterprise.error,
        tag: counts.enterprise.rangeLabel,
        tagColor: "purple",
      },
    ],
    [
      counts.driverCalls,
      counts.enterprise,
      counts.availableCabs,
      counts.attachment,
    ]
  );

  const renderValue = (item: Pick<StatCard, "value" | "loading" | "error">) => {
    if (item.loading) {
      return (
        <div
          style={{
            marginTop: 6,
            marginBottom: 4,
            height: 40,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Skeleton.Button active size="small" style={{ width: 110, height: 30 }} />
        </div>
      );
    }

    if (item.error) {
      return (
        <Tooltip title={item.error}>
          <Title
            level={2}
            style={{
              marginTop: 6,
              marginBottom: 4,
              fontWeight: 800,
              color: THEME.subText,
            }}
          >
            —
          </Title>
        </Tooltip>
      );
    }

    return (
      <Title
        level={2}
        style={{
          marginTop: 6,
          marginBottom: 4,
          fontWeight: 800,
          color: THEME.text,
          opacity: anyLoading ? 0.6 : 1,
          transition: "opacity .2s",
        }}
      >
        {item.value}
      </Title>
    );
  };

  const openPage = (path: string) => openMenuTab(path);

  return (
    <div
      style={{
        minHeight: "100vh",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        padding: screens.xs ? 16 : 10,
        background: `
        radial-gradient(circle at top right,
        rgba(136,70,153,.12),
        transparent 30%),

        radial-gradient(circle at bottom left,
        rgba(181,106,200,.08),
        transparent 25%),

        #F7F8FC
      `,
      }}
    >
      <Card
        variant="borderless"
        styles={{ body: { padding: screens.xs ? 24 : 40 } }}
        style={{
          ...cardStyle,
          marginBottom: 28,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(135deg,#884699 0%,#9C4FB2 55%,#B56AC8 100%)",
        }}
      >

        <div
          style={{
            position: "absolute",
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: "rgba(255,255,255,.08)",
            right: -120,
            top: -120,
          }}
        />

        <div
          style={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(255,255,255,.06)",
            left: -70,
            bottom: -90,
          }}
        />

        <Row gutter={[30, 30]} align="middle" justify="space-between">
          {/* Left */}

          <Col xs={24} lg={15}>
            <Space orientation="vertical" size={6} style={{ width: "100%" }}>
              <Space wrap>
                <Tag
                  color="rgba(255,255,255,.18)"
                  style={{
                    color: "#fff",
                    border: "none",
                    width: "fit-content",
                    padding: "6px 14px",
                    borderRadius: 30,
                    fontWeight: 600,
                  }}
                >
                  <DashboardOutlined /> {greeting}
                </Tag>

                <Tooltip title="Refresh all counts">
                  <Button
                    icon={<RedoOutlined />}
                    loading={anyLoading}
                    onClick={refresh}
                    style={{
                      background: "rgba(255,255,255,.18)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 30,
                      fontWeight: 600,
                    }}
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Space>

              <Title
                level={1}
                style={{
                  color: "#fff",
                  margin: 0,
                  fontWeight: 800,
                  lineHeight: 1.15,
                }}
              >
                Dashboard
              </Title>

              <Text
                style={{
                  color: "rgba(255,255,255,.85)",
                  fontSize: 16,
                  display: "block",
                  maxWidth: 650,
                }}
              >
                Monitor bookings, drivers, vehicles, revenue and
                business performance in real time from one place.
              </Text>

              <Space wrap size={20} style={{ marginTop: 20 }}>
                <Space>
                  <ClockCircleFilled style={{ color: "#fff" }} />

                  <Text style={{ color: "#fff" }}>
                    {today.toLocaleDateString(undefined, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </Text>
                </Space>

                <Space>
                  <FireOutlined style={{ color: "#FFD54F" }} />

                  <Text style={{ color: "#fff" }}>
                    Updated : {lastUpdated ? lastUpdated.format("hh:mm") : "—"}
                  </Text>
                </Space>
              </Space>
            </Space>
          </Col>

          <Col xs={24} lg={9}>
            <Row gutter={[16, 16]}>
              {heroTiles.map((tile) => (
                <Col span={12} key={tile.label}>
                  <Card
                    variant="borderless"
                    styles={{ body: { padding: 18 } }}
                    style={glassTile}
                  >
                    <Text style={{ color: "rgba(255,255,255,.8)" }}>
                      {tile.label}
                    </Text>

                    <Title level={2} style={{ color: "#fff", margin: "8px 0" }}>
                      {tile.error ? "—" : tile.value}
                    </Title>

                    <Tag
                      color={tile.tagColor}
                      style={{ borderRadius: 30, fontWeight: 600 }}
                    >
                      {tile.tag}
                    </Tag>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Card>

      <Row gutter={[24, 24]} justify="space-between" style={{ marginBottom: 30 }}>
        {dashboardStats.map((item, index) => (
          <Col xs={24} sm={12} md={12} lg={8} xl={4} key={item.key}>
            <Card
              variant="borderless"
              styles={{ body: { padding: 22 } }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              onClick={() => openPage(item.path)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openPage(item.path);
                }
              }}
              style={{
                ...cardStyle,
                cursor: "pointer",
                transform:
                  hoveredCard === index ? "translateY(-8px)" : "translateY(0)",
                transition: ".35s ease",
                position: "relative",
              }}
            >
              {/* TOP GLOW */}

              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: 5,
                  width: "100%",
                  background: item.color,
                }}
              />

              <Row justify="space-between" align="middle">
                <Col>
                  <div
                    style={{
                      width: 68,
                      height: 68,
                      borderRadius: 20,
                      background: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 28,
                      boxShadow: "0 12px 30px rgba(0,0,0,.12)",
                    }}
                  >
                    {item.icon}
                  </div>
                </Col>

                <Col>
                  <Tag
                    color="purple"
                    style={{
                      borderRadius: 20,
                      fontWeight: 600,
                      padding: "3px 10px",
                    }}
                  >
                    {item.rangeLabel}
                  </Tag>
                </Col>
              </Row>

              <div style={{ marginTop: 26 }}>
                <Text style={{ color: THEME.subText, fontSize: 15, fontWeight: 500 }}>
                  {item.title}
                </Text>

                {renderValue(item)}

                {item.error ? (
                  <Text type="danger" style={{ fontSize: 12.5 }}>
                    Failed to load
                  </Text>
                ) : (
                  <Text style={{ color: "#9CA3AF" }}>
                    {item.share !== undefined
                      ? `${item.share}% of trips`
                      : item.rangeLabel}
                  </Text>
                )}
              </div>

              <div style={{ marginTop: 18 }}>
                {item.share !== undefined ? (
                  <Progress
                    percent={item.share}
                    showInfo={false}
                    strokeColor={{ "0%": "#884699", "100%": "#B56AC8" }}
                    railColor="#F3F4F6"
                  />
                ) : (
                  <div style={{ height: 22 }} />
                )}
              </div>

              <Divider style={{ margin: "18px 0 16px" }} />

              <Row justify="space-between">
                <Col>
                  <Space>
                    <ArrowRightOutlined style={{ color: THEME.primary }} />

                    <Text strong style={{ color: THEME.primary }}>
                      Open page
                    </Text>
                  </Space>
                </Col>

                <Col>
                  <Text style={{ color: "#9CA3AF" }}>
                    {lastUpdated ? lastUpdated.format("HH:mm:ss") : "—"}
                  </Text>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* Revenue */}

        <Col xs={24} xl={15}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 28 } }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space align="center" wrap>
                  <Title level={4} style={{ marginBottom: 2, fontWeight: 700 }}>
                    Revenue Trend
                  </Title>
                  <SampleTag />
                </Space>

                <Text type="secondary">
                  Monthly revenue performance — awaiting a summary endpoint
                </Text>
              </Col>

              <Col>
                <Button
                  type="primary"
                  style={{
                    background: THEME.primary,
                    borderColor: THEME.primary,
                    borderRadius: 12,
                  }}
                >
                  This Year
                </Button>
              </Col>
            </Row>

            <div style={{ marginTop: 30 }}>
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#884699" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#884699" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#ECECEC" />

                  <XAxis dataKey="month" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />

                  <ChartTooltip />

                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#884699"
                    strokeWidth={4}
                    fill="url(#revenueFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>

        <Col xs={24} xl={9}>
          <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 28 } }}>
            <Space align="center" wrap>
              <Title level={4} style={{ marginBottom: 2, fontWeight: 700 }}>
                Booking Analytics
              </Title>
              <SampleTag />
            </Space>

            <Text type="secondary">
              Daily booking volume — awaiting a summary endpoint
            </Text>

            <div style={{ marginTop: 30 }}>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={bookingData}>
                  <CartesianGrid vertical={false} stroke="#ECECEC" />

                  <XAxis dataKey="day" axisLine={false} tickLine={false} />

                  <YAxis axisLine={false} tickLine={false} />

                  <ChartTooltip />

                  <Bar dataKey="bookings" fill="#884699" radius={[10, 10, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default memo(Dashboard);
